import { useState, useCallback, useRef } from 'react';
import { useToast } from '../components/Toast';
import { ERROR_MESSAGES, USER_ERROR_MESSAGES } from '../utils/constants.js';
// @ts-ignore - JS module without types
import { NetworkService } from '../services/networkService.js';

export interface ErrorInfo {
  type: string;
  message: string;
  field?: string;
  code?: string | number;
  retryable?: boolean;
  timestamp?: Date;
}

export interface ErrorHandlingOptions {
  showToast?: boolean;
  logError?: boolean;
  retryable?: boolean;
  maxRetries?: number;
  retryDelay?: number;
}

export function useErrorHandling() {
  const [errors, setErrors] = useState<Record<string, ErrorInfo>>({});
  const [isRetrying, setIsRetrying] = useState(false);
  const retryCount = useRef<Record<string, number>>({});
  const { showError, showWarning } = useToast();

  const addError = useCallback((
    id: string, 
    error: Partial<ErrorInfo>, 
    options: ErrorHandlingOptions = {}
  ) => {
    const {
      showToast = true,
      logError = true,
      retryable = false
    } = options;

    const errorInfo: ErrorInfo = {
      type: error.type || 'UNKNOWN_ERROR',
      message: error.message || ERROR_MESSAGES.UNKNOWN_ERROR,
      field: error.field,
      code: error.code,
      retryable,
      timestamp: new Date(),
      ...error
    };

    // Log error if enabled
    if (logError) {
      console.error(`Error [${id}]:`, errorInfo);
    }

    // Show toast notification if enabled
    if (showToast) {
      const isNetworkError = NetworkService.isNetworkError(new Error(errorInfo.message));
      
      if (isNetworkError) {
        showError(
          USER_ERROR_MESSAGES.NETWORK_OFFLINE.title,
          USER_ERROR_MESSAGES.NETWORK_OFFLINE.message,
          8000
        );
      } else {
        showError(errorInfo.type, errorInfo.message);
      }
    }

    // Update errors state
    setErrors(prev => ({
      ...prev,
      [id]: errorInfo
    }));

    return errorInfo;
  }, [showError]);

  const removeError = useCallback((id: string) => {
    setErrors(prev => {
      const { [id]: removed, ...rest } = prev;
      return rest;
    });
    
    // Reset retry count for this error
    if (retryCount.current[id]) {
      delete retryCount.current[id];
    }
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
    retryCount.current = {};
  }, []);

  const handleError = useCallback(async (
    error: Error | any,
    context: string = 'unknown',
    options: ErrorHandlingOptions = {}
  ) => {
    const {
      maxRetries = 3,
      retryDelay = 1000,
      retryable = NetworkService.isNetworkError(error)
    } = options;

    // Determine error type and message
    let errorType = 'UNKNOWN_ERROR';
    let errorMessage = ERROR_MESSAGES.UNKNOWN_ERROR;

    if (NetworkService.isNetworkError(error)) {
      errorType = 'NETWORK_ERROR';
      errorMessage = ERROR_MESSAGES.NETWORK_ERROR;
    } else if (error?.message) {
      errorMessage = error.message;
      
      // Map common error patterns to types
      if (error.message.includes('duplicate') || error.message.includes('already exists')) {
        errorType = 'DUPLICATE_ERROR';
      } else if (error.message.includes('validation') || error.message.includes('invalid')) {
        errorType = 'VALIDATION_ERROR';
      } else if (error.message.includes('timeout')) {
        errorType = 'TIMEOUT_ERROR';
        errorMessage = ERROR_MESSAGES.CONNECTION_TIMEOUT;
      } else if (error.message.includes('server') || error.message.includes('503')) {
        errorType = 'SERVER_ERROR';
        errorMessage = ERROR_MESSAGES.SERVER_UNAVAILABLE;
      }
    }

    const errorInfo = addError(context, {
      type: errorType,
      message: errorMessage,
      code: error?.code || error?.status,
      retryable
    }, options);

    return errorInfo;
  }, [addError]);

  const retryOperation = useCallback(async (
    operation: () => Promise<any>,
    context: string,
    options: ErrorHandlingOptions = {}
  ) => {
    const { maxRetries = 3, retryDelay = 1000 } = options;
    
    setIsRetrying(true);
    
    try {
      const result = await NetworkService.withRetry(operation, {
        maxRetries,
        baseDelay: retryDelay,
        onRetry: (error, attempt, maxAttempts) => {
          retryCount.current[context] = attempt;
          showWarning(
            `Retrying... (${attempt}/${maxAttempts})`,
            'Please wait while we try again.',
            3000
          );
        }
      });

      // Success - remove any existing errors for this context
      removeError(context);
      return result;
    } catch (error) {
      // Final failure - handle the error
      await handleError(error, context, options);
      throw error;
    } finally {
      setIsRetrying(false);
    }
  }, [handleError, removeError, showWarning]);

  const getErrorsForField = useCallback((field: string) => {
    return Object.values(errors).filter(error => error.field === field);
  }, [errors]);

  const hasErrors = useCallback((field?: string) => {
    if (field) {
      return getErrorsForField(field).length > 0;
    }
    return Object.keys(errors).length > 0;
  }, [errors, getErrorsForField]);

  const getRetryCount = useCallback((context: string) => {
    return retryCount.current[context] || 0;
  }, []);

  return {
    errors,
    isRetrying,
    addError,
    removeError,
    clearAllErrors,
    handleError,
    retryOperation,
    getErrorsForField,
    hasErrors,
    getRetryCount
  };
}